import { useEffect, useState } from 'react';

/**
 * Recording duration readout, shown from the trigger until the clip is closed.
 *
 * Deliberately a DOM overlay and NOT drawn into the logo compositor: the
 * compositor canvas is what MediaRecorder captures, so anything painted there
 * ends up burned into the guest's keepsake. This is chrome for the person
 * standing in front of the camera, not part of the clip.
 *
 * The bar is progress against the intended clip length rather than the hard
 * recorder cap — the cap is a runaway guard at roughly twice the real duration,
 * so filling against it would look stuck at half.
 */
export function RecordingIndicator({
  startedAt,
  totalMs,
  kiosk = false,
}: {
  /** Wall clock when recording began. */
  startedAt: number;
  /** Intended clip length, used for the progress bar. */
  totalMs: number;
  kiosk?: boolean;
}) {
  const [elapsedMs, setElapsedMs] = useState(() => Date.now() - startedAt);

  useEffect(() => {
    // 100ms: fine enough that the tenths digit does not visibly skip, coarse
    // enough to stay off the render path during the transition.
    const id = window.setInterval(() => setElapsedMs(Date.now() - startedAt), 100);
    return () => clearInterval(id);
  }, [startedAt]);

  const shown = Math.max(0, elapsedMs);
  const seconds = (shown / 1000).toFixed(1);
  const progress = totalMs > 0 ? Math.min(1, shown / totalMs) : 0;

  return (
    <div
      // Pinned to the top-right corner rather than centred across the top. The
      // caw prompt and the transition caption both own the centre of that band,
      // and a centred readout landed directly on top of both.
      className={`pointer-events-none absolute right-3 top-0 z-50 flex flex-col items-end pt-[max(1rem,env(safe-area-inset-top))] ${
        kiosk ? 'gap-3' : 'gap-2'
      }`}
      // Announced once rather than on every tick — a live region here would
      // read the counter aloud ten times a second.
      role="status"
      aria-label="Recording"
    >
      <div
        className={`flex items-center rounded-full bg-ink/70 backdrop-blur-sm ${
          kiosk ? 'gap-3 px-6 py-3 text-2xl' : 'gap-2 px-3 py-1.5 text-sm'
        }`}
      >
        <span
          className={`animate-pulse rounded-full bg-blood ${kiosk ? 'h-4 w-4' : 'h-2.5 w-2.5'}`}
        />
        {/* Helvetica rather than font-mono: Tailwind's mono stack bottoms out
            at Courier New, which the Android panel resolves to a slab-serif.
            tabular-nums still holds the tenths digit steady as it ticks. */}
        <span className="font-body tabular-nums text-bone">{seconds}s</span>
      </div>

      <div
        className={`overflow-hidden rounded-full bg-bone/20 ${
          kiosk ? 'h-1.5 w-48' : 'h-1 w-24'
        }`}
      >
        <div
          className="h-full rounded-full bg-straw transition-[width] duration-100 ease-linear"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </div>
  );
}
